import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '../../context/LanguageContext';

// Logo image URL
const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_datagen-avalon/artifacts/udefoijj_photo_2025-05-26_19-31-49%20%281%29.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, switchLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: t.nav.home, path: '/' },
    { name: t.nav.services, path: '/services' },
    { name: t.nav.forPartners, path: '/advertisers' },
    { name: t.nav.aboutUs, path: '/about' },
    { name: t.nav.contact, path: '/contact' },
  ];

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-slate-900/95 backdrop-blur-xl shadow-lg shadow-slate-900/20'
          : 'bg-slate-900/80 backdrop-blur-md',
      )}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo with uploaded image */}
          <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <div className="relative">
              <img
                src={LOGO_URL}
                alt="Avalon X Media Logo"
                className="w-9 h-9 md:w-10 md:h-10"
                style={{ borderRadius: '10px' }}
              />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-white text-base md:text-lg tracking-tight leading-tight">
                Avalon X Media
              </span>
              <span className="text-[10px] md:text-xs text-slate-400 -mt-0.5 leading-tight">
                GmbH
              </span>
            </div>
          </Link>

          {/* Desktop Navigation - visible on md screens and up */}
          <div className="hidden md:flex items-center gap-0.5 lg:gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  'px-3 lg:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap',
                  location.pathname === link.path
                    ? 'text-emerald-400 bg-emerald-900/30'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800',
                )}>
                {link.name}
              </Link>
            ))}
          </div>

          {/* Actions - Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language Switcher */}
            <div className="flex items-center bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => switchLanguage('en')}
                className={cn(
                  'px-2.5 py-1.5 text-xs font-semibold rounded-md transition-all duration-200',
                  language === 'en'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white',
                )}>
                EN
              </button>
              <span className="text-slate-600 mx-0.5">|</span>
              <button
                onClick={() => switchLanguage('de')}
                className={cn(
                  'px-2.5 py-1.5 text-xs font-semibold rounded-md transition-all duration-200',
                  language === 'de'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white',
                )}>
                DE
              </button>
            </div>

            {/* Book Strategy Call Button - visible on md screens and up */}
            <Link to="/contact" className="hidden md:block">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/40 transition-all duration-300 text-sm px-4 lg:px-6">
                {t.nav.bookStrategyCall}
              </Button>
            </Link>

            {/* Mobile Menu Button - only visible below md */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-slate-300 hover:bg-slate-800 transition-colors duration-200"
              aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - only visible below md */}
        <div
          className={cn(
            'md:hidden overflow-hidden transition-all duration-300 ease-in-out',
            mobileMenuOpen ? 'max-h-[400px] pb-6' : 'max-h-0',
          )}>
          <div className="flex flex-col gap-1 pt-4 border-t border-slate-700">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                  location.pathname === link.path
                    ? 'text-emerald-400 bg-emerald-900/30'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800',
                )}>
                {link.name}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="mt-2">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                {t.nav.bookStrategyCall}
              </Button>
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};
